'use client'

import { createContext, useContext, useState, useEffect } from 'react'

interface CopilotContextType {
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;
  copilotMode: 'chat' | 'feedback';
  setCopilotMode: (mode: 'chat' | 'feedback') => void;
  toggleVisibility: () => void;
}

const CopilotContext = createContext<CopilotContextType>({
  isVisible: false,
  setIsVisible: () => {},
  copilotMode: 'chat',
  setCopilotMode: () => {},
  toggleVisibility: () => {},
})

const isBrowser = typeof window !== 'undefined'

export function CopilotProvider({ children }: { children: React.ReactNode }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [copilotMode, setCopilotMode] = useState<'chat' | 'feedback'>('chat');

  // Load initial state from localStorage
  useEffect(() => {
    if (isBrowser) {
      const stored = localStorage.getItem('copilotVisible');
      if (stored !== null) {
        setIsVisible(stored === 'true');
      }
    }
    setIsLoaded(true);
  }, []);

  // Update localStorage when state changes
  useEffect(() => {
    if (isLoaded && isBrowser) {
      localStorage.setItem('copilotVisible', isVisible.toString());
    }
  }, [isVisible, isLoaded]);

  const toggleVisibility = () => {
    setIsVisible(prev => !prev);
  };

  // Don't render children until initial state is loaded
  if (!isLoaded) return null;

  return (
    <CopilotContext.Provider value={{
      isVisible,
      setIsVisible,
      copilotMode,
      setCopilotMode,
      toggleVisibility,
    }}>
      {children}
    </CopilotContext.Provider>
  );
}

export function useCopilot() {
  const context = useContext(CopilotContext)
  if (context === undefined) {
    throw new Error('useCopilot must be used within a CopilotProvider')
  }
  return context
}
